/**
 * Iter-334 Phase A — Admin Vendor Marketplace moderation.
 * URL: /admin/marketplace/vendors
 *
 * List of vendors with filters, approve/unpublish/suspend actions,
 * plus a bookings tab for platform-wide visibility.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, ExternalLink, Check, X, Ban } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const AUTH = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const fmt = (n) => `$${Number(n || 0).toFixed(2)}`;

export default function AdminMarketplaceVendors() {
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') || 'vendors';
  const pendingOnly = params.get('pending') === '1';

  return (
    <div className="min-h-[100dvh] bg-slate-50">
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <h1 className="text-xl font-bold">Marketplace Moderation</h1>
          <div className="flex gap-2 mt-3">
            <button onClick={() => setParams({ tab: 'vendors' })} className={`px-3 py-1 text-sm rounded ${tab === 'vendors' ? 'bg-teal-600 text-white' : 'text-slate-600 hover:bg-slate-100'}`} data-testid="admin-tab-vendors">Vendors</button>
            <button onClick={() => setParams({ tab: 'bookings' })} className={`px-3 py-1 text-sm rounded ${tab === 'bookings' ? 'bg-teal-600 text-white' : 'text-slate-600 hover:bg-slate-100'}`} data-testid="admin-tab-bookings">Bookings</button>
          </div>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-4 py-6">
        {tab === 'vendors' ? <VendorsList pendingOnly={pendingOnly} /> : <BookingsList />}
      </main>
    </div>
  );
}

function VendorsList({ pendingOnly: initialPending }) {
  const [pendingOnly, setPendingOnly] = useState(initialPending);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const url = pendingOnly ? `${API}/admin/marketplace/vendors?pending_only=1` : `${API}/admin/marketplace/vendors`;
    const r = await axios.get(url, AUTH());
    setItems(r.data); setLoading(false);
  }, [pendingOnly]);
  useEffect(() => { load(); }, [load]);

  const approve = async (id) => {
    await axios.post(`${API}/admin/marketplace/vendors/${id}/approve`, {}, AUTH());
    toast.success('Approved'); load();
  };
  const unpublish = async (id) => {
    if (!window.confirm('Unpublish this vendor?')) return;
    await axios.post(`${API}/admin/marketplace/vendors/${id}/unpublish`, {}, AUTH());
    toast.success('Unpublished'); load();
  };
  const suspend = async (id) => {
    const note = window.prompt('Suspension reason (visible to admin only):');
    if (!note) return;
    await axios.post(`${API}/admin/marketplace/vendors/${id}/suspend`, { note }, AUTH());
    toast.success('Suspended'); load();
  };

  return (
    <div className="space-y-3">
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={pendingOnly} onChange={e => setPendingOnly(e.target.checked)} data-testid="admin-pending-only" />
        Pending approval only
      </label>
      {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : items.length === 0 ? (
        <div className="text-center text-slate-500 py-8">No vendors match.</div>
      ) : (
        <div className="space-y-2">
          {items.map(v => (
            <Card key={v.id} className="p-3" data-testid={`admin-vendor-${v.id}`}>
              <div className="flex flex-wrap items-center gap-3 justify-between">
                <div className="min-w-0 flex-1">
                  <div className="font-semibold">{v.company_name}</div>
                  <div className="text-xs text-slate-500">
                    {v.category_name || '—'} · {v.city || '?'}, {v.state || '?'} · {v.owner_email}
                    {v.stripe_ready ? ' · Stripe ready' : ' · Stripe not set up'}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 items-center">
                  {v.is_published && !v.admin_approved && <span className="text-xs px-2 py-1 rounded-full bg-amber-100 text-amber-800">Pending</span>}
                  {v.is_published && v.admin_approved && <span className="text-xs px-2 py-1 rounded-full bg-emerald-100 text-emerald-800">Live</span>}
                  {!v.is_published && <span className="text-xs px-2 py-1 rounded-full bg-slate-100 text-slate-700">Draft</span>}
                  <Link to={`/vendor/${v.slug}`} target="_blank" className="text-xs text-teal-700 hover:underline inline-flex items-center gap-1">View <ExternalLink className="w-3 h-3" /></Link>
                  {!v.admin_approved && v.is_published && (
                    <Button size="sm" onClick={() => approve(v.id)} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`admin-approve-${v.id}`}><Check className="w-3.5 h-3.5 mr-1" /> Approve</Button>
                  )}
                  {v.is_published && (
                    <Button size="sm" variant="outline" onClick={() => unpublish(v.id)} data-testid={`admin-unpublish-${v.id}`}><X className="w-3.5 h-3.5 mr-1" /> Unpublish</Button>
                  )}
                  <Button size="sm" variant="outline" className="text-red-600" onClick={() => suspend(v.id)} data-testid={`admin-suspend-${v.id}`}><Ban className="w-3.5 h-3.5" /></Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function BookingsList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    axios.get(`${API}/admin/marketplace/bookings`, AUTH())
      .then(r => setItems(r.data))
      .finally(() => setLoading(false));
  }, []);
  if (loading) return <Loader2 className="w-5 h-5 animate-spin" />;
  if (items.length === 0) return <div className="text-center text-slate-500 py-8">No bookings yet.</div>;
  return (
    <div className="space-y-2">
      {items.map(b => (
        <Card key={b.id} className="p-3" data-testid={`admin-booking-${b.id}`}>
          <div className="flex flex-wrap justify-between items-center gap-2">
            <div>
              <div className="font-medium">{b.vendor?.company_name} → {b.guest_name || b.guest_email || 'Guest'}</div>
              <div className="text-xs text-slate-500">
                {new Date(b.event_start_at).toLocaleString()} · {b.status.replace(/_/g, ' ')} · Total {fmt(b.total_amount)} · Paid {fmt(b.amount_paid)}
              </div>
            </div>
            <span className="text-xs px-2 py-1 rounded-full bg-slate-100">{b.status.replace(/_/g, ' ')}</span>
          </div>
        </Card>
      ))}
    </div>
  );
}
